/**
 * SQL Query Service
 * Handles CRUD operations for saved SQL queries with backend API integration
 */

import { PostRequestResolve } from './FetchHelper';
import { isAdminUser } from './authservice';
import URL from './url';

/**
 * Check if current user is admin
 */
const checkIsAdmin = () => {
  return isAdminUser();
};

/**
 * Get all saved queries (both user's and shared)
 * @returns {Promise<Array>} Array of query objects
 */
export const getAllQueries = async () => {
  try {
    const response = await PostRequestResolve(URL.listSavedQueries, null, {});
    return response.queries || [];
  } catch (error) {
    console.error('Error fetching all queries:', error);
    throw error;
  }
};

/**
 * Get queries separated by type (private queries and shared queries)
 * Fetches all queries once and returns them categorized
 * @returns {Promise<{private: Array, shared: Array}>} Object with private and shared query arrays
 */
export const getQueriesByType = async () => {
  try {
    // Get all queries from backend in single call
    const response = await PostRequestResolve(URL.listSavedQueries, null, {});
    const allQueries = response.queries || [];
    
    // Separate based on isShared status only
    const privateQueries = allQueries.filter(q => !q.isShared);
    const sharedQueries = allQueries.filter(q => q.isShared);
    
    return {
      private: privateQueries,
      shared: sharedQueries
    };
  } catch (error) {
    console.error('Error fetching queries:', error);
    throw error;
  }
};

/**
 * Get a specific query by ID
 * @param {string} id - Query ID
 * @returns {Promise<Object|null>} Query object or null
 */
export const getQueryById = async (id) => {
  try {
    // Fetch all queries and find by ID
    const response = await PostRequestResolve(URL.listSavedQueries, null, {});
    const queries = response.queries || [];
    return queries.find(q => q.id === id) || null;
  } catch (error) {
    console.error('Error fetching query by ID:', error);
    throw error;
  }
};

/**
 * Create a new saved query
 * @param {Object} queryData - Query data {name, description, query, folder, isShared}
 * @returns {Promise<Object>} Created query object
 */
export const createQuery = async (queryData) => {
  try {
    // Only admins can create shared queries
    if (queryData.isShared && !checkIsAdmin()) {
      throw new Error('Only administrators can create shared queries');
    }
    
    const payload = {
      name: queryData.name,
      description: queryData.description || '',
      query: queryData.query,
      folder: queryData.folder || '',
      isShared: queryData.isShared || false,
    };
    
    const response = await PostRequestResolve(URL.createSavedQuery, null, payload);
    return response;
  } catch (error) {
    console.error('Error creating query:', error);
    throw error;
  }
};

/**
 * Update an existing query
 * @param {string} id - Query ID
 * @param {Object} updates - Fields to update (name, description, query, folder)
 * @returns {Promise<Object>} Updated query object
 * 
 * NOTE: isShared status cannot be updated through this function.
 * Use toggleQueryShared() instead to change sharing status.
 */
export const updateQuery = async (id, updates) => {
  try {
    // Remove isShared from updates - it should only be changed via toggleQueryShared
    const { isShared, ...allowedUpdates } = updates;
    
    if (isShared !== undefined) {
      console.warn('isShared cannot be updated via updateQuery. Use toggleQueryShared() instead.');
    }
    
    const payload = {
      id: id,
      ...allowedUpdates,
    };
    
    const response = await PostRequestResolve(URL.updateSavedQuery, null, payload);
    return response;
  } catch (error) {
    console.error('Error updating query:', error);
    throw error;
  }
};

/**
 * Delete a query
 * @param {string} id - Query ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteQuery = async (id) => {
  try {
    const payload = { id: id };
    await PostRequestResolve(URL.deleteSavedQuery, null, payload);
    return true;
  } catch (error) {
    console.error('Error deleting query:', error);
    throw error;
  }
};

/**
 * Toggle shared status of a query (Admin only)
 * @param {string} id - Query ID
 * @param {boolean} isShared - New shared status
 * @returns {Promise<Object>} Updated query object
 */
export const toggleQueryShared = async (id, isShared) => {
  try {
    if (!checkIsAdmin()) {
      throw new Error('Only administrators can change query sharing status');
    }
    
    const payload = {
      id: id,
      isShared: isShared,
    };
    
    const response = await PostRequestResolve(URL.toggleQueryShare, null, payload);
    return response;
  } catch (error) {
    console.error('Error toggling query share:', error);
    throw error;
  }
};

/**
 * Check if user can share queries (is admin)
 * @returns {boolean} True if user is admin
 */
export const canShareQueries = () => {
  return checkIsAdmin();
};

/**
 * Search queries by name or content
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Filtered queries
 */
export const searchQueries = async (searchTerm) => {
  try {
    // Fetch all and filter client-side
    const allQueries = await getAllQueries();
    
    if (!searchTerm) {
      return allQueries;
    }
    
    const term = searchTerm.toLowerCase();
    return allQueries.filter(q => 
      q.name.toLowerCase().includes(term) ||
      (q.description && q.description.toLowerCase().includes(term)) ||
      (q.folder && q.folder.toLowerCase().includes(term)) ||
      q.query.toLowerCase().includes(term)
    );
  } catch (error) {
    console.error('Error searching queries:', error);
    throw error;
  }
};
